// The "keep me signed in" secret store — an OS-keychain seam, separate from the (non-secret) BlobStore in
// cache.ts. It holds ONE thing: the revocable refresh token (wiser-accounts 0017) a desktop app trades for a
// fresh session on launch. It NEVER holds the password or key material — a keychain leak is a revocable
// identity token, not permanent decryption of the user's data (see wiser-auth#6 Phase B/C).
//
// The store is available ONLY where a real OS credential store exists (Tauri v2 → keyring crate over macOS
// Keychain / Windows Credential Manager / Linux Secret Service). On the web there is no such store, so the
// default impl reports `available() === false` and the whole feature is simply not offered. Host desktop apps
// inject a real impl at startup via `setSecretStore` (kept out of this package so it stays framework-neutral,
// mirroring the theme/security side-effect policy in session.ts). See README "Keep me signed in (desktop)".

/** A minimal secret-bytes store backed by the OS keychain. All methods are async (native IPC in Tauri). */
export interface SecretStore {
  /** True only where a real OS credential store exists. When false the "keep me signed in" UI is hidden and
   *  no secret is ever written. */
  available(): Promise<boolean>;
  get(key: string): Promise<Uint8Array | null>;
  set(key: string, val: Uint8Array): Promise<void>;
  del(key: string): Promise<void>;
}

/** The default (web / no-keychain) store: unavailable, and inert if something calls it anyway. */
export function unavailableSecretStore(): SecretStore {
  return {
    async available() { return false; },
    async get() { return null; },
    async set() { /* no keychain — silently ignored (feature is gated on available()) */ },
    async del() { /* no-op */ },
  };
}

/** An in-memory, AVAILABLE store for tests (and a reference impl). Not for production — nothing persists. */
export function memorySecretStore(): SecretStore {
  const m = new Map<string, Uint8Array>();
  return {
    async available() { return true; },
    async get(k) { return m.get(k) ?? null; },
    async set(k, v) { m.set(k, v); },
    async del(k) { m.delete(k); },
  };
}

let store: SecretStore = unavailableSecretStore();
/** Inject the host's keychain-backed store (Tauri desktop). Call once at startup. */
export function setSecretStore(s: SecretStore): void { store = s; }

let refreshKey = 'wiser.refresh';
/** Override the refresh-token keychain key (namespace per app: `wiser.refresh:office`). */
export function setRefreshTokenKey(key: string): void { refreshKey = key; }

/** Whether a keychain is present — gate the "keep me signed in" checkbox on this. */
export function keychainAvailable(): Promise<boolean> { return store.available(); }

// Constructed lazily, NOT at module scope: on React Native/Hermes TextEncoder/TextDecoder come from a
// polyfill, and import-time construction would race the host's polyfill loading.

/** Persist the refresh token in the OS keychain (opt-in "keep me signed in"). No-op without a keychain. */
export async function saveRefreshToken(token: string): Promise<void> {
  if (!token) return;
  if (!(await store.available())) return;
  await store.set(refreshKey, new TextEncoder().encode(token));
}

/** The stored refresh token, or null if none / no keychain. */
export async function loadRefreshToken(): Promise<string | null> {
  if (!(await store.available())) return null;
  const bytes = await store.get(refreshKey);
  return bytes ? new TextDecoder().decode(bytes) : null;
}

/** Forget the stored refresh token (opt-out / sign-out / a 401 on exchange). Always safe to call. */
export async function clearRefreshToken(): Promise<void> {
  await store.del(refreshKey);
}
